import React, { Suspense, useEffect, useMemo } from "react";
import MdaeLoader from "../common_components/common_widgets/loader";
import MdaeNavigationBar from "../common_components/common_widgets/navigation_bar";
import { useDispatch, useSelector } from "react-redux";
import MdaePageHeader from "../common_components/common_widgets/mdae-page-header";
import "bootstrap/dist/css/bootstrap.css";
import "./index.scss";
import { isEqual } from "lodash";
import NotifyPopup from "../common_components/common_widgets/notify-popup";
import ConfirmationPopup from "../common_components/common_widgets/confirmation-popup";
import ApplicationEventHandler from "./ApplicationEventHandler";
import { CopyTextTooltip } from "../common_components/common_widgets/copy-text/component";
import NotificationAndSystemModule from "../common_components/common_widgets/notification-and-system-messages-module";
import {
  URL_THEME_MAPPING,
  mdaeNewNavigationBarConfig,
  APP_LAYOUT_THEME,
} from "../common_components/common_widgets/navigation_bar/mdae-navigation-bar-config";
import BellCurveReportParameter from "../common_components/bell-curve-report-parameter/component";
import { GET_DOCUMENT_MANAGEMENT_CLIENT_CONFIG } from "../common_components/document-management/services/get-document-management-client-config";
import { actions as asyncActions } from "async-ops";
import { IS_APP_LOADED } from "./reducers/appReducer";
import { FG_EXTERNAL_USER_TASK_EDITING, hasAnyFeatureGroupAccess, hasAnyInternalAuditWorkFlowAccess } from "../common_components/user-permissions-helper";
import MdaeProgressLoadIndicator from "../common_components/common_widgets/progress-load-indicator/component";
import { initUserpilot, trackUserpilotPage } from "../utils/commmon-services/userpilot-service";
import SuspenceCallBack from "./suspense-callback";

const AnalyticsTheme = React.lazy(() =>
  import("../common_components/common_widgets/analytics-theme")
);
const AuditsTheme = React.lazy(() =>
  import("../common_components/common_widgets/audits-theme")
);
const EatTheme = React.lazy(() =>
  import("../common_components/common_widgets/eat-theme")
);

interface IAppProps {
  isAnalyticsTheme?: boolean;
  isEatTheme?: boolean;
  history?: any;
  children?: React.ReactNode;
}
const setBrowserTitle = (title) => {
  document.getElementById("pnlMdaeBrowserTitle").innerHTML = title;
};

const GetApplicationTheme = (urlTheme: string) => {
  if (urlTheme === APP_LAYOUT_THEME.ANALYTICS) {
    setBrowserTitle("Analytics");
    return AnalyticsTheme;
  } else if (urlTheme === APP_LAYOUT_THEME.EAT) {
    setBrowserTitle("Payer Audit");
    return EatTheme;
  } else {
    setBrowserTitle("Enterprise");
    return AuditsTheme;
  }
};
const getApplicationThemeFromUrl = (props) => {
  for (let prop in URL_THEME_MAPPING) {
    const pages = URL_THEME_MAPPING[prop];
    for (let i = 0; i < pages.length; i++) {
      if (
        mdaeNewNavigationBarConfig.isCurrentURL(
          props.history?.location?.pathname,
          pages[i],
          false
        )
      )
        return prop;
    }
  }
  return APP_LAYOUT_THEME.AUDIT;
};

export const ToggleLoader: React.FC<any> = () => {
  const showCommonLoader = useSelector(
    (state) => state["mdaeCommonReducer"].showCommonLoader,
    isEqual
  );
  const isDisableFocusState = useSelector(
    (state) => state["mdaeCommonReducer"].isDisableFocusState,
    isEqual
  );
  const isAppLoaded = useSelector(
    (state) => state["appReducer"].isAppLoaded,
    isEqual
  );
  return (
    <MdaeLoader
      show={showCommonLoader && isAppLoaded}
      isDisableFocusState={isDisableFocusState}
    ></MdaeLoader>
  );
};
const App: React.FC<IAppProps> = (props) => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(
      asyncActions.asyncOperationStart(GET_DOCUMENT_MANAGEMENT_CLIENT_CONFIG)
    );
  }, []);

  const currentUser = useSelector(
    (state) => state["mdaeNavigationBarReducer"].currentUser,
    isEqual
    );

    useEffect(() => {
        if (!currentUser) return;
        initUserpilot(currentUser);
    }, [currentUser]);

    useEffect(() => {
        trackUserpilotPage();
    }, [props.history?.location?.pathname]);

  const isExternalUser = useMemo(
    () => hasAnyFeatureGroupAccess([FG_EXTERNAL_USER_TASK_EDITING], currentUser),
    [currentUser]
  );

  const urlTheme = getApplicationThemeFromUrl(props);
  const ApplicationTheme = GetApplicationTheme(urlTheme);
  useEffect(() => {
    if(isExternalUser && !checkIsValidExternalUserPage(window.location.href))
      window.location.href = "/audit/mytasks";
  }, [window.location.href, isExternalUser]);
  
  return (
    <Suspense fallback={<SuspenceCallBack></SuspenceCallBack>}>
      <ApplicationTheme>
        <ToggleLoader></ToggleLoader>
        <MdaePageHeader history={props.history}></MdaePageHeader>
        <NotificationAndSystemModule>
          <MdaeNavigationBar
            isAnalyticsTheme={urlTheme === APP_LAYOUT_THEME.ANALYTICS}
            isEatTheme={urlTheme === APP_LAYOUT_THEME.EAT}
            history={props.history}
          >
            {props.children}
          </MdaeNavigationBar>
        </NotificationAndSystemModule>
        <NotifyPopup></NotifyPopup>
        <MdaeProgressLoadIndicator></MdaeProgressLoadIndicator>
        <ConfirmationPopup></ConfirmationPopup>
        <ApplicationEventHandler></ApplicationEventHandler>
        <CopyTextTooltip></CopyTextTooltip>
        <BellCurveReportParameter></BellCurveReportParameter>
      </ApplicationTheme>
    </Suspense>
  );
};

const checkIsValidExternalUserPage = (href: string) => {
  if (href.length > 0) {
    const url = new URL(href);
    var pathName = url.pathname;

    if (pathName.endsWith("/"))
      pathName = pathName.substring(0, pathName.length - 1);

    const validExternalUserPages = [
      "/audit/mytasks",
      "/user/userprofile",
      "/Account/ChangePassword",
      "/audit/taskhq",
      "/externalaudit/taskhq"
    ];
    for (let validPage of validExternalUserPages) {
      if (pathName?.toLowerCase()?.endsWith(validPage)) return true;
    }
  }

  return false;
};

export default App;
